import type { ReactElement } from 'react';
import { Text, View, XStack, YStack } from 'tamagui';

import TierNotice from '@/components/TierNotice';
import PSBadge from '@/components/ui/PSBadge';
import PSCircleButton from '@/components/ui/PSCircleButton';
import PSIcon from '@/components/ui/PSIcon';
import PSRichText from '@/components/ui/PSRichText';
import { fontSize } from '@/constants/tokens';
import type {
  MembershipTier,
  PSPrice,
  ShortParticipationRequest,
  TierBookability,
} from '@/src/api/eventsApi';
import type { PendingMembershipState } from '@/src/hooks/usePendingMembership';
import { formatDateWithYear } from '@/src/utils/dateFormatter';
import { useRouter } from 'expo-router';
import PSText from './ui/PSText';

/**
 * TicketTier — one purchasable ticket option in the "Available tickets" list
 * (molecule).
 *
 * A bordered card: the tier name, its price, a badge per membership allowed to
 * book it, a description, and one action — a quantity stepper, a "Sold out"
 * badge, or a `TierNotice` explaining why this one is out of reach.
 *
 * ── The membership badges ──
 * `membershipsAppliedToTickettier` is a LIST, and every name in it is printed. It
 * used to be one `isAccessPass` boolean rendering a fixed "Access Pass only"
 * chip, which was only ever right while there was one membership to sell; with
 * a Trial Pass and two Access Pass grades, a card that says "Access Pass only"
 * while a Trial Pass would in fact get you in is simply wrong. So the card
 * prints what it's given and special-cases nothing.
 *
 * NULL is the ungated tier — anyone can book, so there are no badges and no row.
 * That's the prop's own distinction, not an emptiness check: `null` and `[]`
 * would look identical here but mean opposite things upstream, so the card
 * branches on the null and never on the length.
 *
 * The badges say who MAY book, which is independent of whether THIS reader can
 * (that's `bookability`): a member sees the badges AND a stepper. They are bare
 * names, with no "only" or "members" lead-in — worth revisiting if the row reads
 * as a list of perks rather than a restriction.
 *
 * ── It is render-only now, and that's a change ──
 * This card used to own the action decision itself: a 3-boolean ladder over
 * sold-out / access-pass / has-pass, defended in its docstring on the grounds
 * that the rule was small and closed. It isn't either of those any more. The
 * server now sends `bookability` — its verdict plus the ONE winning reason,
 * already ranked (see `TierBookability`) — so there is no decision left to
 * concentrate here. The card switches on the reason it's handed. Consequences
 * worth knowing before "simplifying" this back:
 *
 *   - Precedence is the SERVER's. A tier can be sold out AND membership-gated;
 *     which of those the user reads is not this component's call, and it must
 *     not re-derive one from the other's data.
 *   - The card no longer knows who the user is. `userExists` / `hasAccessPass`
 *     are gone — "logged out" arrives as a `unauthenticated` reason like any
 *     other, so there's no auth state to hold and no way to disagree with the
 *     server about it.
 *
 * What it DOES own is the copy for each reason: the reason is what makes the
 * words correct, so the map lives with the switch that picks it (the
 * `SecretMenuCard` precedent — the component owns its per-state caption, the
 * caller owns which state). Numbers inside that copy are the server's own
 * (`usage`, the plan's name / price / description), never invented here.
 *
 * ── Muting ──
 * Only `sold_out` mutes the card: the tier is gone, so the copy steps back and
 * the badge is the point. Every other blocked reason is a tier the reader could
 * still get to, so it stays full strength behind its notice. The name/price are
 * our own `<Text>`s and read the muted colour directly (RN text doesn't
 * cascade); the description is `PSRichText`, whose token colours we don't
 * control per-node, so it's dimmed with `opacity` on a wrapper (opacity DOES
 * cascade). The badge itself stays full strength — a sold-out tier is dimmed,
 * but the REASON it's dimmed should read clearly.
 *
 * The stepper's ceiling is `numTickets`, which does NOT account for tickets the
 * user already holds — the payload only reports a seat allowance once it's
 * exhausted (as `max_seats_per_user_exhausted`), so a user partway through
 * theirs can over-add here and be refused at booking time. Known gap, left
 * deliberately; it closes with a remaining-allowance field, not with client
 * arithmetic.
 *
 * ── Approval-only tiers ──
 * `requiresGuestApproval` blanks the Add/stepper slot even while the tier is
 * bookable — `eventCart` derives it straight onto the cart, one seat exactly,
 * the moment its slot is selected, so there's no tap left for this card to
 * offer. A `request_pending_approval` verdict (an existing request from a
 * prior visit) draws its own foot notice instead, same as every other
 * blocked reason — and once that request is approved, the tier reads
 * `booking_confirmed_cannot_reapply` instead, whose notice links straight to
 * the booking rather than back to the (now moot) request.
 */
type TicketTierProps = {
  name: string;
  price: PSPrice;
  /** Stored rich-text markdown — rendered via PSRichText. */
  description: string;
  /** One badge per membership this tier requires, or NULL when anyone can book
   * it. A tier fact, NOT the reader's state — see the docstring. */
  membershipsAppliedToTickettier: MembershipTier[] | null;
  /** The server's verdict + reason. Picks the whole action area. */
  bookability: TierBookability;
  /**
   * Approval-only tiers cap at one seat and skip the Add/stepper entirely —
   * `eventCart` derives it straight onto the cart the moment its slot is
   * picked, so there's nothing left for a tap here to do. Leaves the
   * top-right slot empty even while bookable; `renderNotice` still runs for a
   * `request_approval_pending` verdict.
   */
  requiresGuestApproval: boolean;
  /**
   * The viewer's own latest application to THIS tier, or null. Only read for
   * `booking_confirmed_cannot_reapply` below — the reason's own `request.id`
   * is the participation request's id, not the booking's, so the booking id
   * to link to comes from here instead (see `ShortParticipationRequest`).
   */
  participationRequest: ShortParticipationRequest | null;
  quantity: number;
  /** The stepper's ceiling — the tier's `maxSeatsPerUser`. */
  numTickets: number;
  /** Stepper intents — the screen clamps and stores the resulting quantity. */
  onIncrement: () => void;
  onDecrement: () => void;
  /**
   * The notice CTAs. Each hands back what its action needs, because the card
   * has already narrowed the reason and the screen would otherwise re-narrow it.
   */
  onViewBookings: (bookings: { id: string }[]) => void;
  /**
   * The viewer's unconfirmed purchase, if any. The server still sends
   * `membership_required` for them, but they can't buy another plan until this
   * one is finished or cancelled, so that notice points here instead.
   */
  pendingMembership?: PendingMembershipState | null;
  /** `dark` on the members-only `$brandDark` band. */
  surface?: 'light' | 'dark';
};

export default function TicketTier({
  name,
  price,
  description,
  membershipsAppliedToTickettier,
  bookability,
  requiresGuestApproval,
  participationRequest,
  quantity,
  numTickets,
  onDecrement,
  onIncrement,
  onViewBookings,
  pendingMembership = null,
  surface = 'light',
}: TicketTierProps) {
  // The two inline, top-right treatments. Everything else is a foot notice.
  const soldOut = bookability.reason?.type === 'sold_out';
  const bookableInline = bookability.isBookable && !requiresGuestApproval;
  // Below the stepper's floor, "+ Add" stands in for it — reached only by
  // decrementing back to 0, so no extra state: quantity alone drives the swap.
  const showStepper = bookableInline && quantity > 0;
  const showAddButton = bookableInline && quantity === 0;

  const dark = surface === 'dark';
  // $brand is too dim on $brandDark; the band's accent is $brandSecondary.
  const accent = dark ? '$brandSecondary' : '$brand';
  const accentBorder = dark ? '$color.brandSecondary' : '$color.brand';
  const idleBorder = dark ? '$color.panelTranslucent' : '$borderColor';
  const idleIcon = dark ? '$textWhite' : '$textDark';

  // RN text doesn't cascade — each <Text> below reads this off the flag.
  const nameColor = dark
    ? soldOut
      ? '$textWhiteMuted'
      : '$textWhite'
    : soldOut
      ? '$textDarkMuted'
      : '$textDark';
  const router = useRouter();

  /**
   * The foot block for whatever state we're in, or null when the top-right slot
   * already said it (sold out) or there's nothing to say (a plain stepper).
   *
   * Typed `ReactElement | null` rather than `ReactNode` on purpose: `ReactNode`
   * admits `undefined`, so a reason added to the union later would fall out of
   * this switch SILENTLY. This way it's a compile error.
   */
  function renderNotice(): ReactElement | null {
    if (bookability.isBookable) return null;

    const reason = bookability.reason;
    switch (reason.type) {
      // Both are already said elsewhere: a sold-out tier shows the badge in the
      // top-right slot, and a past slot never reaches a card at all (the screen
      // drops those slots — see the reason's own docstring).
      case 'sold_out':
      case 'slot_in_past':
        return null;

      case 'request_pending_approval': {
        const { request } = reason;
        return (
          <TierNotice
            surface={surface}
            title="Your booking is pending approval"
            body={`We'll notify you via email once your booking request is approved.`}
            action={{
              label: 'View details',
              onPress: () => router.push(`/requests/${request.id}`),
            }}
          />
        );
      }

      case 'booking_confirmed_cannot_reapply': {
        // `reason.request.id` is the participation request's id, not the
        // booking's — the booking id lives on the tier's own
        // `participationRequest` (see this prop's docstring), so a viewer
        // lands on their actual ticket instead of the request receipt.
        const bookingId = participationRequest?.bookingId;
        return (
          <TierNotice
            surface={surface}
            title="You already have a booking!"
            body="The organizer approved your booking request, and the ticket is in your account."
            action={
              bookingId
                ? {
                    label: 'View booking',
                    onPress: () => router.push(`/bookings/${bookingId}`),
                  }
                : undefined
            }
          />
        );
      }

      case 'unauthenticated':
        return (
          <TierNotice
            surface={surface}
            title="Log in to book"
            body="Tickets are held in your name, so we'll need you signed in before you can pick any."
            // action={{ label: 'Log in', onPress: onLogin }}
          />
        );

      case 'max_seats_per_user_exhausted': {
        const { usage, bookings } = reason;
        return (
          <TierNotice
            surface={surface}
            title="You've booked your limit"
            body={`This ticket is capped at ${usage.limit} per person, and you've booked ${usage.used}.`}
            action={{
              // Plural off the BOOKINGS, not the seat count: one booking can
              // hold several seats, and it's bookings we're linking to.
              label: bookings.length === 1 ? 'View booking' : 'View bookings',
              onPress: () => onViewBookings(bookings),
            }}
          />
        );
      }

      case 'membership_required': {
        if (pendingMembership) {
          return (
            <TierNotice
              surface={surface}
              title={
                pendingMembership.planName
                  ? `Your payment for ${pendingMembership.planName} is pending`
                  : 'Your membership payment is pending'
              }
              body="Please complete or cancel it first"
              action={{
                label: 'Continue',
                onPress: () =>
                  router.push(`/my-memberships/${pendingMembership.id}`),
              }}
            />
          );
        }
        // Only the server knows which plans unlock this tier, so a lone plan
        // is named and several read as "any pass".
        const plans = reason.membershipsRequiredToBook;
        const onlyPlan = plans.length === 1 ? plans[0] : null;
        return (
          <TierNotice
            surface={surface}
            title={
              onlyPlan
                ? `Get ${onlyPlan.name} to book this`
                : 'Get any pass to book this'
            }
            body=""
            action={{
              label: onlyPlan ? 'View membership' : 'View all memberships',
              onPress: () => {
                router.push({
                  pathname: '/memberships',
                  params: { section: 'plans' },
                });
              },
            }}
          />
        );
      }

      case 'limit_reached': {
        const { usage, membershipsRequiredToBook: plan } = reason;
        // Up to two sentences, and BOTH can be missing: no reset date when the
        // membership won't renew, no upgrade line when they're on the top plan.
        // That pair is the genuine dead end, so it gets a sentence of its own
        // rather than an empty block.
        const sentences: string[] = [];
        if (usage.periodResetsAt) {
          sentences.push(
            `Your allowance resets on ${formatDateWithYear(usage.periodResetsAt)}.`
          );
        }
        if (plan && plan.length > 0)
          sentences.push(`Upgrade to book this now.`);

        return (
          <TierNotice
            surface={surface}
            title={`You've used ${usage.used} of ${usage.limit} ${usage.label} ${
              usage.limit === 1 ? 'booking' : 'bookings'
            }`}
            body={sentences.join(' ')}
            action={
              plan
                ? {
                    label: `View memberships`,
                    onPress: () => {
                      router.push({
                        pathname: '/memberships',
                        params: { section: 'plans' },
                      });
                    },
                  }
                : undefined
            }
          />
        );
      }
    }
  }

  return (
    <YStack
      borderWidth="$borderWidth.hairline"
      borderColor={dark ? '$color.panelTranslucent' : '$color.borderColor'}
      rounded="$base"
      bg={dark ? '$transparent' : '$background'}
      p="$md"
      gap="$space.md"
    >
      <XStack justify="space-between" items="flex-start" gap="$sm">
        <YStack flex={1} gap="$sm">
          <PSText.Body2 color={nameColor}>{name}</PSText.Body2>

          <XStack items="center" gap="$sm" flexWrap="wrap">
            <PSText.Body1 color={nameColor}>{price.display}</PSText.Body1>
          </XStack>

          {/* One badge per required membership, on their own row below the
            price, wrapping onto the next line when the names outgrow it. Sold
            out, the "Sold out" badge should own attention, so these are dimmed
            rather than competing with it.

            The ROW itself is conditional, not just its contents: an empty
            XStack still counts as a flex item, so the parent's `gap` would open
            a phantom line of space above the description on every ungated tier. */}
          {membershipsAppliedToTickettier ? (
            <XStack gap="$space.sm" flexWrap="wrap">
              {membershipsAppliedToTickettier.map((membership) => (
                <PSBadge
                  key={membership.name}
                  variant={membership.kind === 'trial' ? 'trial' : 'brand'}
                  label={`${membership.name}`}
                  disabled={soldOut}
                />
              ))}
            </XStack>
          ) : null}
        </YStack>

        {/* Inline top-right slot: "Sold out" badge, "+ Add", or the stepper
          once quantity leaves 0. Every other state is a full-width notice
          below the description, so this slot is empty in those cases. */}
        {soldOut ? (
          <PSBadge label="Sold out" variant="outline" size="large" />
        ) : showAddButton ? (
          <XStack
            onPress={onIncrement}
            items="center"
            gap="$sm"
            height={38}
            px="$md"
            rounded="$base"
            borderWidth="$borderWidth.hairline"
            borderColor={accentBorder}
            pressStyle={{ opacity: 0.85 }}
          >
            <PSIcon name="add" size={fontSize.base} color={accent} />
            <PSText.H3 color={accent}>Add</PSText.H3>
          </XStack>
        ) : showStepper ? (
          /* Stepper: PSCircleButton disables the minus at 0 and dims itself. */
          <XStack items="center" gap="$md">
            <PSCircleButton
              filled={!dark}
              disabled={quantity === 0}
              borderColor={quantity === 0 ? idleBorder : accentBorder}
              onPress={onDecrement}
            >
              <PSIcon
                name="remove"
                size={fontSize.base}
                color={quantity === 0 ? idleIcon : accent}
              />
            </PSCircleButton>
            <Text
              fontFamily="$body"
              fontSize="$base"
              color={accent}
              text="center"
              width={fontSize.base}
            >
              {quantity}
            </Text>
            <PSCircleButton
              filled={!dark}
              onPress={onIncrement}
              disabled={quantity === numTickets}
              borderColor={quantity === numTickets ? idleBorder : accentBorder}
            >
              <PSIcon
                name="add"
                size={fontSize.base}
                color={quantity === numTickets ? idleIcon : accent}
              />
            </PSCircleButton>
          </XStack>
        ) : null}
      </XStack>

      {/* Description is stored rich text, so PSRichText renders it (it owns its
        own token colours — base $textDark, full strength). Unlike the name/price
        above, we can't flip its colour off `soldOut` per-<Text> (it's not our
        markup), so when the tier IS sold out we DIM it with opacity instead: RN
        opacity DOES cascade, so one wrapper covers the whole block. */}
      <View opacity={soldOut ? 0.35 : 1}>
        <PSRichText markdown={description} surface={surface} />
      </View>

      {renderNotice()}
    </YStack>
  );
}
